import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import { Save, CreditCard } from 'lucide-react';

export default function GymSaasPlansConfig() {
    const { t } = useTranslation();
    const { getAuthenticatedApi } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [features, setFeatures] = useState([]);
    const [plans, setPlans] = useState([]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const api = getAuthenticatedApi();
            const res = await api.get('/admin_gym_saas_plans.php');
            setFeatures(res.data?.features || []);
            setPlans(res.data?.plans || []);
            setMessage(null);
        } catch (err) {
            setMessage({
                type: 'error',
                text: err.response?.data?.error || err.message || t('admin.saasPlans.loadError'),
            });
        } finally {
            setLoading(false);
        }
    }, [getAuthenticatedApi, t]);

    useEffect(() => {
        load();
    }, [load]);

    const togglePlanEnabled = (planId) => {
        setPlans((prev) =>
            prev.map((p) => (p.id === planId ? { ...p, enabled: !p.enabled } : p)),
        );
    };

    const setPlanListPrice = (planId, raw) => {
        const trimmed = String(raw).trim();
        const parsed = trimmed === '' ? null : Number.parseFloat(trimmed.replace(',', '.'));
        setPlans((prev) =>
            prev.map((p) =>
                p.id === planId
                    ? {
                          ...p,
                          list_price_monthly:
                              trimmed === '' || Number.isNaN(parsed) || parsed <= 0 ? null : Math.round(parsed * 100) / 100,
                      }
                    : p,
            ),
        );
    };

    const toggleFeature = (planId, featureKey) => {
        setPlans((prev) =>
            prev.map((p) => {
                if (p.id !== planId) {
                    return p;
                }
                return {
                    ...p,
                    features: {
                        ...p.features,
                        [featureKey]: !p.features?.[featureKey],
                    },
                };
            }),
        );
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const api = getAuthenticatedApi();
            await api.put('/admin_gym_saas_plans.php', { plans });
            setMessage({ type: 'success', text: t('admin.saasPlans.saveSuccess') });
            await load();
        } catch (err) {
            setMessage({
                type: 'error',
                text: err.response?.data?.error || err.message || t('admin.saasPlans.saveError'),
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-[50vh] items-center justify-center">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-4 sm:p-6">
            <div className="flex flex-wrap items-start gap-4 mb-6">
                <CreditCard className="h-8 w-8 text-indigo-600 shrink-0" />
                <div className="flex-1 min-w-0">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {t('admin.saasPlans.title')}
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {t('admin.saasPlans.description')}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-60"
                >
                    <Save className="h-4 w-4" />
                    {saving ? t('admin.saasPlans.saving') : t('admin.saasPlans.save')}
                </button>
            </div>

            {message && (
                <div
                    className={`mb-4 rounded-lg px-4 py-3 text-sm ${
                        message.type === 'success'
                            ? 'bg-emerald-50 text-emerald-900 border border-emerald-200'
                            : 'bg-red-50 text-red-900 border border-red-200'
                    }`}
                >
                    {message.text}
                </div>
            )}

            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700 bg-indigo-50/80 dark:bg-indigo-950/30">
                            <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-200 min-w-[200px]">
                                {t('admin.saasPlans.pricingRow')}
                            </th>
                            {plans.map((plan) => (
                                <th key={`price-${plan.id}`} className="p-3 text-center min-w-[168px] align-top">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300 mb-2 capitalize">
                                        {plan.name || plan.id}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {t('admin.saasPlans.stripePrice')}
                                    </div>
                                    <div className="mt-1 font-semibold text-gray-900 dark:text-white tabular-nums">
                                        {plan.stripe_price_monthly != null
                                            ? `€${Number(plan.stripe_price_monthly).toFixed(2)}`
                                            : '-'}
                                    </div>
                                    {plan.stripe_price_id && (
                                        <div className="mt-2 mx-auto max-w-[200px] text-left rounded-md bg-gray-100 dark:bg-gray-900/80 px-2 py-1.5 border border-gray-200 dark:border-gray-600">
                                            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                {t('admin.saasPlans.stripePriceId')}
                                            </div>
                                            <code
                                                className="block mt-0.5 text-[11px] font-mono leading-snug break-all text-gray-800 dark:text-indigo-200"
                                                title={plan.stripe_price_id}
                                            >
                                                {plan.stripe_price_id}
                                            </code>
                                        </div>
                                    )}
                                    <label className="block mt-3 text-xs text-gray-600 dark:text-gray-300 text-left font-medium">
                                        {t('admin.saasPlans.listPrice')}
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={plan.list_price_monthly ?? ''}
                                        onChange={(e) => setPlanListPrice(plan.id, e.target.value)}
                                        placeholder="99"
                                        className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-500 bg-white text-gray-900 placeholder:text-gray-400 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 px-2 py-1.5 text-sm text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                                    />
                                </th>
                            ))}
                        </tr>
                        <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                            <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-200 min-w-[200px]">
                                {t('admin.saasPlans.colFeature')}
                            </th>
                            {plans.map((plan) => (
                                <th key={plan.id} className="p-3 text-center min-w-[120px]">
                                    <div className="font-semibold text-gray-900 dark:text-white capitalize">
                                        {plan.name || plan.id}
                                    </div>
                                    <label className="inline-flex items-center gap-1.5 mt-2 text-xs font-normal cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={plan.enabled}
                                            onChange={() => togglePlanEnabled(plan.id)}
                                            className="rounded border-gray-300"
                                        />
                                        {t('admin.saasPlans.planVisible')}
                                    </label>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {features.map((feature) => (
                            <tr
                                key={feature.feature_key}
                                className="border-b border-gray-100 dark:border-gray-700/80"
                            >
                                <td className="p-3 text-gray-800 dark:text-gray-200">
                                    <div className="font-medium">{feature.label}</div>
                                    {feature.route_path && (
                                        <div className="text-xs text-gray-500 font-mono mt-0.5">
                                            {feature.route_path}
                                        </div>
                                    )}
                                </td>
                                {plans.map((plan) => (
                                    <td key={`${plan.id}-${feature.feature_key}`} className="p-3 text-center">
                                        <input
                                            type="checkbox"
                                            checked={!!plan.features?.[feature.feature_key]}
                                            disabled={!plan.enabled}
                                            onChange={() => toggleFeature(plan.id, feature.feature_key)}
                                            className="h-4 w-4 rounded border-gray-300 disabled:opacity-40"
                                        />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                {t('admin.saasPlans.hint')}
            </p>
        </div>
    );
}
